/**
 * StatusDotLabel — Indicador de status no padrão "bolinha colorida + palavra".
 *
 * Modelo visual adotado na listagem de categorias: substitui o Badge balão
 * por uma bolinha colorida seguida de texto em cor de foreground (preto no
 * tema claro), reduzindo ruído visual e mantendo leitura rápida do estado.
 *
 * Uso:
 *   <StatusDotLabel tone="success" label="Ativo" />
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-26 v1.1.0 - Adicionados tons `danger` (vermelho) e `info`
 *            (azul) para atender ao modal de Vistoria (reprovada/em andamento).
 *   RELEASE: v1.0.0 - Criação inicial (success/warning/neutral).
 */
import { cn } from '@/lib/utils';

/** Tons semânticos suportados e a cor da bolinha correspondente (reutilizado como barra lateral nas listagens) */
export const TONE_DOT: Record<StatusDotTone, string> = {
  success: 'bg-emerald-500', // ativo / vigente / aprovado
  warning: 'bg-amber-500', // rascunho / pausado / aguardando
  neutral: 'bg-zinc-400', // arquivado / desativado / pendente
  danger: 'bg-red-500', // reprovado / correção solicitada
  info: 'bg-sky-500', // em andamento
};

export type StatusDotTone = 'success' | 'warning' | 'neutral' | 'danger' | 'info';

interface StatusDotLabelProps {
  /** Tom semântico que define a cor da bolinha */
  tone: StatusDotTone;
  /** Texto exibido ao lado da bolinha */
  label: string;
  /** Classes extras (ex.: ocultar em mobile) */
  className?: string;
}

export function StatusDotLabel({ tone, label, className }: StatusDotLabelProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap',
        className,
      )}
    >
      <span className={cn('h-2 w-2 rounded-full shrink-0', TONE_DOT[tone])} />
      {label}
    </span>
  );
}
